/**
 * SqueezeRadarPill — Round P107b (Feb 2026)
 *
 * A small orange pill that renders next to a ticker in the Capital
 * Quality Top 3 card and the Top Dividend Movers card when — and only
 * when — that ticker is currently in the Squeeze Radar snapshot for
 * the viewing user.
 *
 * Design mirrors the "⚠ Most Extreme" sort toggle in SqueezeRadarStrip:
 *   text-orange-700 dark:text-orange-400 ring-orange-400/50
 *   bg-orange-50/60 dark:bg-orange-950/30
 *
 * Click behaviour: dispatches a global custom event that
 * SqueezeRadarStrip listens for. The strip handles the "scroll into
 * view + center the chip + pulse until dismissed" UX.
 *
 * Data source is `/api/dashboard/squeeze-radar` — the same endpoint the
 * strip uses. So if the pill shows, the chip is guaranteed to exist in
 * the strip (respects Plus-gating for non-admin users automatically).
 * Single fetch is shared across all callers via a module-level promise.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_TTL_MS = 2 * 60_000; // 2 min — matches typical strip refresh cadence

// Module-level cache so 10 rows in Capital Quality + 50 rows in Top
// Movers all share ONE HTTP request (and one Set for O(1) lookups).
let _cache = null;         // { fetchedAt: number, tickers: Set<string> }
let _inflight = null;      // Promise<Set<string>>

function _fetchTickerSet() {
  if (_cache && Date.now() - _cache.fetchedAt < CACHE_TTL_MS) {
    return Promise.resolve(_cache.tickers);
  }
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/dashboard/squeeze-radar`, { timeout: 5000, withCredentials: true })
    .then((r) => {
      const rows = r?.data?.top || [];
      const set = new Set(rows.map((row) => String(row.ticker || '').toUpperCase()).filter(Boolean));
      _cache = { fetchedAt: Date.now(), tickers: set };
      return set;
    })
    .catch(() => new Set())
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * Hook — returns a Set<string> of ticker symbols currently in the
 * squeeze radar. Empty set on error or while loading (so consumers
 * simply render nothing).
 */
export function useSqueezeRadarTickers() {
  const [tickers, setTickers] = useState(() => (_cache ? _cache.tickers : new Set()));
  useEffect(() => {
    let cancelled = false;
    _fetchTickerSet().then((set) => {
      if (!cancelled) setTickers(set);
    });
    return () => { cancelled = true; };
  }, []);
  return tickers;
}

/**
 * Pill component. Renders nothing if the ticker isn't in the radar.
 */
export default function SqueezeRadarPill({ ticker, className = '' }) {
  const tickerSet = useSqueezeRadarTickers();
  const upper = String(ticker || '').toUpperCase();
  if (!upper || !tickerSet.has(upper)) return null;

  const handleClick = (e) => {
    e.stopPropagation();  // don't trigger the parent row's onStockClick
    e.preventDefault();
    // Dispatched globally; SqueezeRadarStrip subscribes and drives the
    // scroll+center+pulse UX. Loose coupling — the strip doesn't need
    // to know who fired the event.
    window.dispatchEvent(
      new CustomEvent('squeeze-radar:focus', { detail: { ticker: upper } })
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`${upper} is in the Squeeze Radar — click to jump there.`}
      data-testid={`squeeze-radar-pill-${upper}`}
      className={`inline-flex items-center gap-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ring-1 transition-colors text-orange-700 dark:text-orange-400 ring-orange-400/50 bg-orange-50/60 dark:bg-orange-950/30 hover:bg-orange-100/80 dark:hover:bg-orange-950/60 ${className}`}
    >
      <ArrowRight className="w-3 h-3" />
      <Zap className="w-3 h-3" />
      <span>Squeeze Radar</span>
    </button>
  );
}
